<template>
  <div class="pc__search-filter">
    <div class="filter-title">
      <van-icon name="filter-o" size="22" /> Search filter
    </div>

    <div class="filter-info">
      <div class="filter-info-title">Price</div>
      <div class="filter-value-list filter-price-box">
        <ClientOnly>
          <van-field v-model="filterPrice.priceFrom" label="" placeholder="Min" />
          <span>——</span>
          <van-field v-model="filterPrice.priceTo" label="" placeholder="Max" />
          <van-button type="primary" @click="handlePriceFilterApplyClick">Apply</van-button>
        </ClientOnly>
      </div>
    </div>

    <div class="filter-info" v-if="categoryList.length > 0">
      <SearchFilterWidget
        title-text="Suggestions"
        :list="categoryList"
        @filter:click="handleFilterValueClick('frontCategoryId', $event)"
      />
    </div>
    <div class="filter-info" v-if="currentCategories.length > 0">
      <SearchFilterWidget
        title-text="Category"
        :list="currentCategories"
        @filter:click="handleFilterValueClick('categoryIds', $event)"
      />
    </div>

    <div class="filter-info" v-if="guarantees.length > 0">
      <SearchFilterWidget
        title-text="Service & Guarantee"
        :list="guarantees"
        @filter:click="handleFilterValueClick('guarantees', $event)"
      />
    </div>

    <div class="filter-info" v-if="deliveries.length > 0">
      <SearchFilterWidget
        title-text="Shipped From"
        :list="deliveries"
        @filter:click="handleFilterValueClick('shipped', $event)"
      />
    </div>

    <div class="filter-info" v-for="(item, idx) in attrList" :key="idx">
      <SearchFilterWidget
        :title-text="item.name"
        :list="item.values"
        name-key="value"
        @filter:click="(item: any, subIdx: number) => handleAttrClick(item, idx, subIdx)"
      />
    </div> 
  </div>
</template>

<script setup lang="ts">
import { type ISearchParams } from '@/api/models/system'
import SearchFilterWidget from './SearchFilterWidget.vue';

interface ICategory { id: number, name: string, subCategories?: ICategory, bindUrl: string }
// interface IAttr { name: string, values: { value: string }[] }
interface IProps {
  // 推荐的类目（当前类目下的子类目）
  categoryList: ICategory[]
  filter: any
}
type filterValueType = 'frontCategoryId' | 'categoryIds' | 'guarantees' | 'shipped'
const props = defineProps<IProps>()
const emits = defineEmits<{
  (event: 'filter:change', params: ISearchParams): void
}>()
const route = useRoute()
// 存储上一次的查询参数
let lastFilterParams: IAnyObject = {}
const deliveries = ref<any[]>([])
const guarantees = ref<any[]>([])
const attrList = ref<any[]>([])

// 当前结果的类目集合
const currentCategories = ref<any[]>([])

let isInit = ref<boolean>(false)
const initFilters = () => {
  if(Object.keys(props.filter).length > 0) {
    isInit.value = true
  }
  deliveries.value = (props.filter?.deliveries || []).map((c: any) => {
    let select = !!lastFilterParams[c.paramName]

    return { select, ...c }
  })
  guarantees.value = (props.filter?.guarantees || []).map((c: any) => {
    let select = !!lastFilterParams[c.paramName]
    return { select, ...c }
  })
  currentCategories.value = (props.filter.categories ? props.filter.categories[0].subCategories : [])
    .map((c: any) => {
      let select = (lastFilterParams.categoryIds || []).includes(c.id)

      return { select, ...c}
    })
  
  attrList.value = (props.filter?.attrs || []).map((c: any) => {
    const lastAttrFilters = JSON.parse(lastFilterParams.attrFilters || '[]')
    const selectVals = (lastAttrFilters.find((v: any) => v.name === c.name)?.values || []).map((v: any) => v.value)
    
    c.values.forEach((v: any) => { 
      v.select = selectVals.includes(v.value)
    })

    return c
  })
}

// 初始化过滤参数
watchEffect(() => {
  if(isInit.value) {
    return
  }
  
  initFilters()
})

// 价格过滤
const filterPrice = ref<ISearchParams>({ priceFrom: '', priceTo: '' })

/** 类目页 frontCategoryId：有 Suggestions 时只传最后一个，否则传 URL id */
const buildFrontCategoryIdParam = (routeFcId: number, suggestionIds: number[] = []) => {
  const subIds = suggestionIds.filter((fid) => fid !== routeFcId)
  return subIds.length ? subIds[subIds.length - 1] : routeFcId
}

const handleSearchFilter = () => {
  const _p: ISearchParams = Object.assign({}, filterPrice.value, filterParams.value)
  _p.attrFilters = JSON.stringify(getFilterAttr())

  const { id: routeCategoryId, form: routeForm } = route.query
  const routeFcNum = routeCategoryId ? Number(String(routeCategoryId)) : null
  const suggestionIds = (filterParams.value.frontCategoryId as number[] | undefined) || []

  delete _p.frontCategoryId
  if (routeForm === 'category' && routeFcNum) {
    _p.frontCategoryId = buildFrontCategoryIdParam(routeFcNum, suggestionIds)
  } else if (suggestionIds.length) {
    _p.frontCategoryId = suggestionIds[suggestionIds.length - 1]
  }

  lastFilterParams = _p

  emits('filter:change', _p)
}
const handlePriceFilterApplyClick = () => {
  handleSearchFilter()
}
// 获取属性过滤参数
const getFilterAttr = (): any[] => {
  const selectAttr: any[] = []
  attrList.value.forEach((attr: any) => {
    const selectValues = attr.values.filter((attrVal: any) => attrVal.select)
    if(selectValues.length > 0) {
      selectAttr.push({ name: attr.name, values: selectValues.map((v: any) => ({ value: v.value}))})
    }
  })
  
  return selectAttr
}

// 属性过滤
const handleAttrClick = (item: any, baseIdx: number, valIdx: number) => {
  item.select = !item.select
  handleSearchFilter()
}

// 参数过滤
const filterParams = ref<IAnyObject>({})
const handleFilterValueClick = (type: filterValueType, item: any) => {
  if(['guarantees', 'shipped'].includes(type)) {
    item.select = !item.select
    if(filterParams.value[item.paramName] === item.paramValue) {
      filterParams.value[item.paramName] = ''
    } else {
      filterParams.value[item.paramName] = item.paramValue
    }
  } else if (type === 'frontCategoryId') {
    // Suggestions 单选：再次点击取消，否则切换到当前项
    const willSelect = !item.select
    props.categoryList.forEach((c: any) => { c.select = false })
    item.select = willSelect
    if (willSelect) {
      filterParams.value.frontCategoryId = [item.id]
    } else {
      const { id } = route.query
      filterParams.value.frontCategoryId = id ? [Number(id)] : []
    }
  } else {
    item.select = !item.select
    !filterParams.value[type] && (filterParams.value[type] = [])

    // 如果已选中，则取消选中
    const idx = filterParams.value[type].findIndex((id: number) => item.id === id)
    if(idx >= 0) {
      filterParams.value[type].splice(idx, 1)
    } else {
      filterParams.value[type].push(item.id)
    }

    const { backCid } = route.query

    if(filterParams.value[type].length <= 0) {
      (backCid && type === 'categoryIds') && filterParams.value[type].push(Number(backCid));
    }
  }

  handleSearchFilter()
}

const clearFilter = () => {
  lastFilterParams = {}
  filterParams.value = {}
  filterPrice.value.priceFrom = ''
  filterPrice.value.priceTo = ''
  props.categoryList.forEach((c: any) => { c.select = false })

  initFilters()
  // isInit.value = false
}

defineExpose({
  clearFilter
})
</script>

<style lang="scss" scoped>
$padding: 30px;
.pc__search-filter{
  width: 100%;
  // padding: 0 30px;
  background-color: #fff;
}
.filter-title{
  display: flex;
  align-items: center;
  font-weight: 600;
  padding: $padding;
  font-size: 32px;
  margin-bottom: 20px;
}
.filter-info{
  .filter-info-title{
    font-weight: 600;
    font-size: 32px;
    color: #666;
    padding: 0 $padding;
    margin: 10px 0;
  }
  .filter-value-list{
    .filter-value{
      line-height: 60px;
      font-size: 26px;
      padding: 0 $padding;
      cursor: pointer;
      border-left: 4px solid transparent;
      transition: all .15s;
      &:hover{
        color: var(--pc-default-color);
        background-color: rgba($color: #DD3131, $alpha: 0.2);
      }
    }
    .filter-value-act{
      color: var(--pc-default-color);
      background-color: rgba($color: #DD3131, $alpha: 0.2);
      border-color: var(--pc-default-color);
    }
  }
  .filter-price-box{
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    min-height: 60px;
    padding: 0 30px;
    span{
      color: #cacaca;
      margin: 0 10px;
    }
  }
  .filter-price-box:deep(.van-field){
    border: 1px solid #cacaca;
    padding: 5px 20px;
    width: 160px;
  }
  .filter-price-box:deep(.van-button--normal){
    height: 62px;
    margin-left: 20px;
  }
}
.show-more{
  text-align: center;
  color: var(--pc-default-color);
  padding: 20px;
  font-size: 26px;
  cursor: pointer;
  transition: all .15s;
  &:hover{
    background: rgba($color: #DD3131, $alpha: 0.2);
  }
}
</style>